/**
 * How short a list card may be made before the page scrolls instead.
 *
 * A list screen is a fixed-height column: the card takes what is left of the
 * frame after the heading and the toolbar, and the table scrolls inside it.
 * That works until what is above it grows — status cards, a toolbar on two
 * lines, a window two thirds the height of the designer's — and then the card
 * keeps giving way until the rows are a sliver behind the pager, on a page with
 * no scrollbar of its own to reach them with. The help desk hit it first
 * because it carries the most above the table; nothing about it is particular
 * to the help desk.
 *
 * So the card is given a floor, and the floor is never higher than the card
 * actually needs: `min(FLOOR, what this table would be if nothing squeezed
 * it)`. A list of one row keeps being one row tall — the empty band under a
 * short table was a real complaint and this does not bring it back — while a
 * full page refuses to go below something readable and lets the column overflow
 * the frame, which is what puts the scrollbar on the page.
 */

import { type RefObject, useEffect, useState } from "react";

/** Roughly four rows and a header: enough to read a table and to scroll it. */
export const TABLE_FLOOR = 240;

/**
 * The card's `min-height`, in pixels, or `undefined` before anything is
 * measured (server render, first paint, a card that is not on screen).
 *
 * `card` is the box being floored; `view` is the scrolling box inside it, and
 * the table inside *that* is what the floor is read from — its height is what
 * the card would be if nothing squeezed it. The difference between the card's
 * height and the scroller's is the card's own furniture — the pager, mostly —
 * which has to be added back or the floor is short by that much.
 */
export function useTableFloor(
  card: RefObject<HTMLElement | null>,
  view: RefObject<HTMLElement | null>,
  floor: number = TABLE_FLOOR,
): number | undefined {
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const box = card.current;
    const scroller = view.current;
    if (!box || !scroller) return;

    const measure = () => {
      const furniture = Math.max(0, box.clientHeight - scroller.clientHeight);
      // The table's own height, not the scroller's `scrollHeight`. A scroller
      // reports a `scrollHeight` of at least its own height, so once the floor
      // has stretched it the floor is measuring itself: a full list raised it
      // to 240, a search cut the table to one row, and the card stayed 240 with
      // a band of nothing under the row — the very thing this was written not
      // to do. The table does not stretch with its scroller, so its height is
      // the one that still answers "how tall would this be if nothing squeezed
      // it". Falls back to the scroller where there is no table to measure.
      const table = scroller.firstElementChild;
      const content = table ? table.scrollHeight : scroller.scrollHeight;
      const natural = furniture + content;
      // Nothing has been laid out — a card that is not on screen, or a test
      // environment with no layout at all. A floor of zero is not a floor, and
      // pinning the card to it would be a worse answer than leaving it alone.
      if (natural <= 0) {
        setHeight(undefined);
        return;
      }
      const next = Math.min(floor, natural);
      // Only a real change, and never a sub-pixel one: this sets a height on a
      // box the observer is watching, and a loop of one-pixel corrections is
      // how a resize observer becomes a spinning fan.
      setHeight((was) => (was !== undefined && Math.abs(was - next) <= 1 ? was : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(scroller);
    // The rows change without either box changing size — a page turned, a
    // filter applied — and the table's own height is what the floor is read
    // from, so the table is watched too.
    const table = scroller.firstElementChild;
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [card, view, floor]);

  return height;
}
